import { copyToClipboard, downloadBlob } from '@/utils/download';
import { Maximize, Minimize, Copy, Download, X } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface TextViewDialogProps {
  visible: boolean;
  title: string;
  textContent: string;
  onClose: () => void;
  downloadFilename?: string;
}

export const TextViewDialog: FC<TextViewDialogProps> = ({
  visible,
  title,
  textContent,
  onClose,
  downloadFilename = 'file.txt',
}) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(textContent);
    if (success) {
      toast.success('Copied to clipboard');
    } else {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    try {
      const blob = new Blob([textContent], { type: 'text/plain' });
      downloadBlob(blob, downloadFilename);
      toast.success('Downloaded successfully');
    } catch (_error) {
      toast.error('Failed to download');
    }
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full pr-2">
      <span className="font-semibold text-lg">{title}</span>
      <Button
        icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        onClick={() => setIsFullscreen((prev) => !prev)}
        text={true}
        rounded={true}
        size="small"
        title={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltip={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltipOptions={{ position: 'left' }}
        aria-label={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        pt={{
          root: {
            style: { outline: 'none', boxShadow: 'none' },
            className: 'focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          },
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onClose}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-3xl'}`}
      dismissableMask={true}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        root: { className: isFullscreen ? 'h-full fixed inset-0' : '' },
        content: { className: isFullscreen ? 'h-full flex flex-col' : '' },
      }}
    >
      <div className={`flex flex-col gap-4 ${isFullscreen ? 'h-full' : ''}`}>
        {/* Action buttons */}
        <div className="flex gap-2 justify-end">
          <Button
            label="Copy"
            icon={<Copy size={16} className="mr-2" />}
            onClick={handleCopy}
            outlined={true}
            size="small"
          />
          <Button
            label="Download"
            icon={<Download size={16} className="mr-2" />}
            onClick={handleDownload}
            outlined={true}
            size="small"
          />
          <Button label="Close" icon={<X size={16} className="mr-2" />} onClick={onClose} size="small" />
        </div>

        {/* Text content */}
        <div className={`bg-gray-50 rounded-lg p-4 overflow-auto ${isFullscreen ? 'flex-1' : 'max-h-96'}`}>
          <pre className="text-xs font-mono text-gray-800 whitespace-pre-wrap break-words">{textContent}</pre>
        </div>
      </div>
    </Dialog>
  );
};
